import { ArrowRight, CheckCircle, PanoramaFishEyeOutlined, RemoveCircle } from '@mui/icons-material';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import { Typography, Divider, Grid, Collapse, Button, Tooltip, Chip } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DateTime } from 'luxon';
import { CaseType, SessionType } from '../../types';
import SessionsList from './SessionsList';
import { getDisplayCaseState, getDisplayCaseStateNotes } from '../ClientsTab/ClientsTable';
import { DialogsContext } from '../../context/dialogsContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { ClientsContext } from '../../context/clientsContext';
import ConfirmDialog from '../ConfirmDialog';
import { CurrentCohortSubscriptionContext } from '../../context/currentCohortSubscriptionContext';
import { CoursesIds } from '../../constants';

// A case is "auto-unsubmitted" when its latest status was authored by the
// sweep rather than a person. `is_system` comes from the server (the sweep is
// the only writer that leaves `setter` empty), so the note wording is free to
// change without breaking this.
const isAutoUnsubmittedCase = (rowCase: CaseType): boolean => {
    if (!rowCase.statuses || rowCase.statuses.length === 0) return false;
    const latest = _.maxBy(rowCase.statuses, 'created_date');
    return !!latest && latest.state === 'UNSUBMITTED' && latest.is_system === true;
};

interface CaseRowItemProps {
    rowCase: CaseType;
    rowCaseSessions: SessionType[];
    toggleStatusRow: (rowCaseId: number) => void;
    toggleSessionRow: (rowCaseId: number) => void;
    openStatusRows: number[];
    openSessionRows: number[];
}


const CaseRowItem = ({
    rowCase,
    rowCaseSessions,
    toggleStatusRow,
    toggleSessionRow,
    openStatusRows,
    openSessionRows
}: CaseRowItemProps) => {
    const createdDate = DateTime.fromISO(rowCase.created_date).toLocaleString(DateTime.DATE_SHORT);
    const { showManageCaseDialog } = useContext(DialogsContext);
    const { clients, resendWaiver } = useContext(ClientsContext);
    const [showResendCaseWaiverDialog, setShowResendCaseWaiverDialog] = useState(false);
    const [caseStatus, setCaseStatus] = useState<string | null>(null);
    const { currentCohortSubscription } = useContext(CurrentCohortSubscriptionContext);

    // Detect an auto-unsubmit event so we can render the SYSTEM chip and
    // auto-open the note. Memoized so it doesn't re-eval on every render.
    const isAutoUnsubmit = useMemo(() => isAutoUnsubmittedCase(rowCase), [rowCase]);

    const isStatusOpen = openStatusRows.includes(rowCase.id);
    const isSessionsOpen = openSessionRows.includes(rowCase.id);

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const isEmail = (email: string): boolean => {
        return emailRegex.test(email);
    };

    useEffect(() => {
        setCaseStatus(getDisplayCaseState(rowCase));
    }, [rowCase]);

    const RowButton = ({ label }: { label: string }) => {
        return (
            <Button sx={{ m: 0, p: 0, ml: 1 }}>{label}</Button>
        )
    }

    const iconStyle = { fontSize: 14 };

    const getWaiverResult = () => {
        const client = clients.find((c) => c.id === (typeof rowCase.client === 'number' ? rowCase.client : rowCase.client.id));
        if (client?.email && isEmail(client?.email)) {
            const wr = client?.waivers_results.find((wr) => wr.case_id === rowCase.id && wr.waiver_id === 2);
            if (wr?.accepted === 'Y') {
                return (
                    <Tooltip title='Client accepted healing waiver terms'>
                        <CheckCircle color='success' sx={iconStyle} />
                    </Tooltip>
                )
            } else if (wr?.accepted === 'N') {
                return (
                    <Tooltip title='Client has declined to accept healing waiver terms'>
                        <RemoveCircle color='error' sx={iconStyle} />
                    </Tooltip>
                )
            } else {
                return (
                    <Tooltip title='Client has not completed healing waiver'>
                        <PanoramaFishEyeOutlined color='action' sx={iconStyle} />
                    </Tooltip>
                )
            }
        } else {
            return <></>;
        }
    }

    const GridDivider = () => {
        return (
            <Grid item xs={12} sm={12} md={12} >
                <Divider sx={{ my: 1 }} />
            </Grid>
        )
    }

    return (
        <Box key={rowCase.id} sx={{ flexDirection: 'row' }}>
            <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
                <Box m={2} sx={{ flexDirection: 'column' }}>
                    <Grid container spacing={1}>
                        <Grid item container xs={12} sm={12} md={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                {getWaiverResult() && <Button disableFocusRipple disableRipple variant={'text'} sx={{ m: 0, p: 0, minWidth: 0, pb: 0.7, pr: 0.5 }} onClick={() => {
                                    const client = clients.find((c) => c.id === rowCase.client);
                                    const wr = client?.waivers_results.find((wr) => wr.case_id === rowCase.id && wr.waiver_id === 2);
                                    if (wr?.accepted !== 'Y') {
                                        setShowResendCaseWaiverDialog(true);
                                    }
                                }}>
                                    {getWaiverResult()}
                                </Button>}
                                <Typography color={'CaptionText'} variant='overline' sx={{ fontWeight: 'bold' }}>NAME</Typography>
                            </Box>
                            <Typography color={'CaptionText'} variant='subtitle1'>{rowCase.name}</Typography>
                        </Grid>
                        <Grid item container xs={12} sm={12} md={12} >
                            <GridDivider />
                            <Grid item xs={6} sm={6} md={2}>
                                <Typography color={'CaptionText'} variant='overline' sx={{ fontWeight: 'bold' }}>TYPE</Typography>
                                <Typography color={'CaptionText'} variant='subtitle1'>{rowCase.type}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={6} md={2}>
                                <Typography color={'CaptionText'} variant='overline' sx={{ fontWeight: 'bold' }}>STATUS</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                                    <Typography color={'CaptionText'} variant='subtitle1'>{caseStatus}</Typography>
                                    {isAutoUnsubmit && (
                                        <Tooltip title="Automatically unsubmitted by the system — this case can no longer contribute to your course. See status description for details.">
                                            <Chip
                                                // Focusable so the explanation is reachable by keyboard
                                                // and tap, not only by hovering a mouse.
                                                tabIndex={0}
                                                icon={<SmartToyRoundedIcon sx={{ fontSize: 14 }} />}
                                                label='SYSTEM'
                                                size='small'
                                                sx={{
                                                    height: 20,
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    letterSpacing: 0.5,
                                                    bgcolor: '#FFF3E0',
                                                    color: '#E65100',
                                                    '& .MuiChip-icon': { color: '#E65100', ml: 0.5 },
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={6} sm={6} md={2}>
                                <Typography color={'CaptionText'} variant='overline' sx={{ fontWeight: 'bold' }}>REMOTE</Typography>
                                <Typography color={'CaptionText'} variant='subtitle1'>{rowCase.remote === 'Y' ? 'yes' : 'no'}</Typography>
                            </Grid>
                            {rowCase.cohort.course.id === CoursesIds.CPH_ID &&
                                <Grid item xs={6} sm={6} md={2}>
                                    <Typography color={'CaptionText'} variant='overline' sx={{ fontWeight: 'bold' }}>LASER CRYSTAL</Typography>
                                    <Typography color={'CaptionText'} variant='subtitle1'>{rowCase.crystal_used === 'Y' ? 'yes' : 'no'}</Typography>
                                </Grid>
                            }
                            <Grid item xs={6} sm={6} md={2}>
                                <Typography color={'CaptionText'} variant='overline' sx={{ fontWeight: 'bold' }}>CREATED</Typography>
                                <Typography color={'CaptionText'} variant='subtitle1'>{createdDate}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={6} md={2}>
                                <Typography color={'CaptionText'} variant='overline' sx={{ fontWeight: 'bold' }}>CATEGORIES</Typography>
                                <Stack direction="row" spacing={1}>
                                    {rowCase.categories && rowCase.categories.length > 0 ?
                                        rowCase.categories.map((c) => <Chip key={c.id} label={c.name} size='small' variant='outlined' />) : <Typography>none</Typography>
                                    }
                                </Stack>
                            </Grid>
                            <Grid item xs={6} sm={6} md={2}>
                                <Box sx={{
                                    display: 'flex', flexDirection: 'row'
                                }}>
                                    {caseStatus !== 'approved' && currentCohortSubscription?.state === 'ACTIVE' && (

                                        <Button
                                            onClick={() => showManageCaseDialog({
                                                show: true, caseId: rowCase.id
                                            })}
                                            sx={{ mt: 0, pt: 0, mr: 0, pr: 0, mb: 0, pb: 0 }}
                                        >
                                            <Typography color={'CaptionText'} variant='overline' sx={{ fontWeight: 'bold', textAlign: 'center' }}>Manage</Typography>
                                            <ArrowRight sx={{ fontSize: 24, mb: 0.45 }} />
                                        </Button>
                                    )}

                                </Box>
                            </Grid>
                            <GridDivider />
                        </Grid>

                        {caseStatus !== 'none' && (
                            <>
                                <Grid item xs={12} sm={12} md={12}>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                        <Typography color={'CaptionText'} variant='overline' sx={{ fontWeight: 'bold' }}>Status Description</Typography>
                                        <Box pb={0.4} onClick={() => toggleStatusRow(rowCase.id)} sx={{ cursor: 'pointer' }}>
                                            <RowButton label={isStatusOpen ? 'close' : 'open'} />
                                        </Box>
                                    </Box>
                                    <Collapse sx={{ width: '100%' }} in={isStatusOpen} unmountOnExit>
                                        <Typography>{getDisplayCaseStateNotes(rowCase)}</Typography>
                                    </Collapse>
                                </Grid>
                                <GridDivider />
                            </>
                        )}
                        <Grid item xs={12} sm={12} md={12}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <Typography color={'CaptionText'} variant='overline' sx={{ fontWeight: 'bold' }}>{`${rowCaseSessions.length} Sessions`}</Typography>
                                <Box pb={0.4} onClick={() => toggleSessionRow(rowCase.id)} sx={{ cursor: 'pointer' }}>
                                    <RowButton label={isSessionsOpen ? 'close' : 'open'} />
                                </Box>
                            </Box>
                            <Collapse sx={{ width: '100%' }} in={isSessionsOpen} unmountOnExit>
                                <SessionsList clientCase={rowCase} caseSessions={rowCaseSessions} />
                            </Collapse>
                        </Grid>
                    </Grid>
                </Box >
            </Box >
            <ConfirmDialog
                open={showResendCaseWaiverDialog}
                title='Case Waiver'
                message='Would you like to resend case waiver?'
                onConfirm={() => {
                    setShowResendCaseWaiverDialog(false);
                    resendWaiver(rowCase.id);
                }}
                onCancel={() => {
                    setShowResendCaseWaiverDialog(false);
                }}
            />
        </Box >
    )
}

export default CaseRowItem;